import React, { useEffect, useState } from 'react'
import {
  Box, Flex, HStack, VStack, Text, Heading, Button, Skeleton, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, Textarea, FormControl, FormLabel, FormHelperText, Input, Center,
  type BoxProps,
} from '@chakra-ui/react'
import { formatNumber } from '@utils/format'

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------
export function PageHeader({
  eyebrow, title, subtitle, actions,
}: { eyebrow?: string; title: string; subtitle?: React.ReactNode; actions?: React.ReactNode }) {
  return (
    <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'flex-end' }} gap={4} mb={6}>
      <Box minW={0}>
        {eyebrow && (
          <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.08em" textTransform="uppercase" color="brand.600" mb={1}>
            {eyebrow}
          </Text>
        )}
        <Heading as="h1" size="lg" color="gray.900" letterSpacing="-0.02em">{title}</Heading>
        {subtitle && <Text mt={1} color="gray.500" fontSize="sm" maxW="2xl">{subtitle}</Text>}
      </Box>
      {actions && <HStack spacing={3} flexShrink={0} flexWrap="wrap">{actions}</HStack>}
    </Flex>
  )
}

export function Panel({
  title, subtitle, actions, children, p = 5, flush = false, ...rest
}: { title?: string; subtitle?: string; actions?: React.ReactNode; children: React.ReactNode; p?: number; flush?: boolean } & BoxProps) {
  return (
    <Box
      bg="white" border="1px solid" borderColor="gray.100" borderRadius="2xl"
      boxShadow="0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)" overflow="hidden" {...rest}
    >
      {(title || actions) && (
        <Flex px={p} pt={p} pb={flush ? p : 0} justify="space-between" align="center" gap={3} borderBottom={flush ? '1px solid' : undefined} borderColor="gray.100">
          <Box minW={0}>
            {title && <Text fontWeight="semibold" color="gray.900" fontSize="md">{title}</Text>}
            {subtitle && <Text fontSize="sm" color="gray.500">{subtitle}</Text>}
          </Box>
          {actions}
        </Flex>
      )}
      <Box p={flush ? 0 : p} pt={title && !flush ? 4 : flush ? 0 : p}>{children}</Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------
export function Money({
  value, size = 'md', muted = false, signed = false, hideCurrency = false,
}: { value: number; size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'; muted?: boolean; signed?: boolean; hideCurrency?: boolean }) {
  const sizes: Record<string, { n: string; c: string }> = {
    sm: { n: 'sm', c: 'xs' }, md: { n: 'md', c: 'xs' }, lg: { n: 'xl', c: 'sm' }, xl: { n: '3xl', c: 'md' }, '2xl': { n: '5xl', c: 'lg' },
  }
  const s = sizes[size]
  const negative = value < 0
  const sign = signed ? (negative ? '−' : value > 0 ? '+' : '') : negative ? '−' : ''
  return (
    <HStack spacing={1.5} align="baseline" display="inline-flex" color={muted ? 'gray.500' : undefined}>
      {!hideCurrency && <Text as="span" fontSize={s.c} fontWeight="medium" opacity={0.7}>TZS</Text>}
      <Text as="span" fontSize={s.n} fontWeight="semibold" letterSpacing="-0.02em" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {sign}{formatNumber(Math.abs(value))}
      </Text>
    </HStack>
  )
}

type Tone = 'brand' | 'blue' | 'orange' | 'red' | 'gray' | 'purple'
const TONES: Record<Tone, { bg: string; fg: string; ring: string }> = {
  brand: { bg: 'brand.50', fg: 'brand.700', ring: 'brand.200' },
  blue: { bg: 'blue.50', fg: 'blue.700', ring: 'blue.200' },
  orange: { bg: 'orange.50', fg: 'orange.700', ring: 'orange.200' },
  red: { bg: 'red.50', fg: 'red.700', ring: 'red.200' },
  gray: { bg: 'gray.100', fg: 'gray.700', ring: 'gray.200' },
  purple: { bg: 'purple.50', fg: 'purple.700', ring: 'purple.200' },
}

export function StatTile({
  label, value, hint, icon, tone = 'brand', loading, onClick, children,
}: { label: string; value?: React.ReactNode; hint?: React.ReactNode; icon?: React.ReactNode; tone?: Tone; loading?: boolean; onClick?: () => void; children?: React.ReactNode }) {
  const t = TONES[tone]
  return (
    <Box
      bg="white" border="1px solid" borderColor="gray.100" borderRadius="2xl" p={5} onClick={onClick}
      boxShadow="0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)"
      cursor={onClick ? 'pointer' : 'default'} transition="all .15s ease"
      _hover={onClick ? { borderColor: t.ring, transform: 'translateY(-1px)', boxShadow: 'md' } : undefined}
    >
      <Flex justify="space-between" align="flex-start" gap={3}>
        <Box minW={0}>
          <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase" color="gray.500">{label}</Text>
          {loading ? <Skeleton h="8" w="28" mt={2} borderRadius="md" /> : (
            <Box mt={1.5} fontSize="2xl" fontWeight="semibold" color="gray.900" letterSpacing="-0.02em">{value}</Box>
          )}
          {hint && !loading && <Text as="div" mt={1} fontSize="sm" color="gray.500">{hint}</Text>}
        </Box>
        {icon && (
          <Center w={10} h={10} borderRadius="xl" bg={t.bg} color={t.fg} flexShrink={0}>{icon}</Center>
        )}
      </Flex>
      {children}
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------
export function Pill({ tone = 'gray', children, dot = true }: { tone?: Tone; children: React.ReactNode; dot?: boolean }) {
  const t = TONES[tone]
  return (
    <HStack as="span" display="inline-flex" spacing={1.5} px={2.5} py={0.5} borderRadius="full" bg={t.bg} color={t.fg}
      fontSize="xs" fontWeight="semibold" whiteSpace="nowrap" lineHeight="1.6">
      {dot && <Box w="1.5" h="1.5" borderRadius="full" bg="currentColor" />}
      <span>{children}</span>
    </HStack>
  )
}

const STATUS: Record<string, Record<string, { tone: Tone; label: string }>> = {
  operator: {
    active: { tone: 'brand', label: 'Active' },
    pending_review: { tone: 'orange', label: 'Pending review' },
    suspended: { tone: 'red', label: 'Suspended' },
    rejected: { tone: 'gray', label: 'Rejected' },
  },
  payout: {
    requested: { tone: 'orange', label: 'Requested' },
    approved: { tone: 'blue', label: 'Approved · sending' },
    paid: { tone: 'brand', label: 'Paid' },
    rejected: { tone: 'red', label: 'Rejected' },
    failed: { tone: 'red', label: 'Failed' },
    cancelled: { tone: 'gray', label: 'Cancelled' },
  },
  vehicle: {
    approved: { tone: 'brand', label: 'Approved' },
    pending: { tone: 'orange', label: 'Awaiting approval' },
    rejected: { tone: 'red', label: 'Rejected' },
  },
  ledger: {
    earning: { tone: 'brand', label: 'Fare' },
    refund: { tone: 'red', label: 'Refund' },
    payout: { tone: 'blue', label: 'Withdrawal' },
    payout_reversal: { tone: 'gray', label: 'Returned' },
    adjustment: { tone: 'purple', label: 'Adjustment' },
    penalty: { tone: 'red', label: 'Penalty' },
  },
}

export function StatusPill({ kind, status }: { kind: keyof typeof STATUS; status: string }) {
  const s = STATUS[kind]?.[status] ?? { tone: 'gray' as Tone, label: status }
  return <Pill tone={s.tone}>{s.label}</Pill>
}

// ---------------------------------------------------------------------------
// Empty + loading
// ---------------------------------------------------------------------------
export function EmptyState({
  icon, title, body, action,
}: { icon?: React.ReactNode; title: string; body?: React.ReactNode; action?: React.ReactNode }) {
  return (
    <VStack spacing={3} py={14} px={6} textAlign="center">
      {icon && <Center w={14} h={14} borderRadius="2xl" bg="gray.50" color="gray.400">{icon}</Center>}
      <Text fontWeight="semibold" color="gray.800">{title}</Text>
      {body && <Text fontSize="sm" color="gray.500" maxW="sm">{body}</Text>}
      {action}
    </VStack>
  )
}

export function SkeletonRows({ rows = 5, h = 12 }: { rows?: number; h?: number }) {
  return (
    <VStack spacing={3} p={5} align="stretch">
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} h={h} borderRadius="lg" />)}
    </VStack>
  )
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------
export function ConfirmModal({
  isOpen, onClose, title, body, confirmLabel, tone = 'brand', loading, onConfirm,
  reasonLabel, reasonRequired = false, inputLabel, inputPlaceholder, inputHelp, inputRequired = false,
}: {
  isOpen: boolean; onClose: () => void; title: string; body?: React.ReactNode; confirmLabel: string
  tone?: 'brand' | 'red' | 'blue'; loading?: boolean
  onConfirm: (values: { reason: string; input: string }) => void
  reasonLabel?: string; reasonRequired?: boolean
  inputLabel?: string; inputPlaceholder?: string; inputHelp?: string; inputRequired?: boolean
}) {
  const [reason, setReason] = useState('')
  const [input, setInput] = useState('')
  useEffect(() => { if (isOpen) { setReason(''); setInput('') } }, [isOpen])
  const blocked = (reasonLabel && reasonRequired && reason.trim().length < 3) || (inputLabel && inputRequired && !input.trim())
  const scheme = tone === 'red' ? 'red' : tone === 'blue' ? 'blue' : 'brand'

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader pb={1}>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {body && <Box color="gray.600" fontSize="sm" mb={4}>{body}</Box>}
          <VStack spacing={4} align="stretch">
            {inputLabel && (
              <FormControl isRequired={inputRequired}>
                <FormLabel fontSize="sm">{inputLabel}</FormLabel>
                <Input value={input} onChange={e => setInput(e.target.value)} placeholder={inputPlaceholder} autoFocus />
                {inputHelp && <FormHelperText>{inputHelp}</FormHelperText>}
              </FormControl>
            )}
            {reasonLabel && (
              <FormControl isRequired={reasonRequired}>
                <FormLabel fontSize="sm">{reasonLabel}</FormLabel>
                <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={500} />
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" color="gray.600" onClick={onClose}>Cancel</Button>
          <Button
            colorScheme={scheme} bg={`${scheme === 'brand' ? 'brand' : scheme}.500`} isLoading={loading} isDisabled={!!blocked}
            onClick={() => onConfirm({ reason: reason.trim(), input: input.trim() })}
          >
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

/** Small uppercase label used above values inside panels. */
export function Label({ children }: { children: React.ReactNode }) {
  return <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.06em" textTransform="uppercase" color="gray.500">{children}</Text>
}

export const tableHead = {
  fontSize: 'xs', fontWeight: 'semibold', letterSpacing: '0.06em', color: 'gray.500', bg: 'gray.50',
  borderColor: 'gray.100', textTransform: 'uppercase' as const, py: 3,
}
